// public/js/features/assets/trip-share.js
// The share box inside an expanded trip: create a secret link, see the ones
// that exist, revoke them (docs/trip-detection.md, "Share links").
//
// A link works without a login, so the box says so plainly rather than
// leaving the user to find out when it is already in a customer's inbox.
import { escapeHtml } from '../../utils/escape.js';
import { openTripShareModal } from './trip-share-modal.js';

const fmtDate = (iso) => iso
  ? new Date(iso).toLocaleDateString(navigator.language, { dateStyle: 'medium' })
  : '—';

const STATE_LABEL = {
  ok: ['badge-soft-green', 'active'],
  expired: ['badge-soft-gray', 'expired'],
  revoked: ['badge-soft-red', 'revoked'],
};

function linkRow(s, origin) {
  const [cls, label] = STATE_LABEL[s.state] || STATE_LABEL.expired;
  return `
    <div class="d-flex flex-wrap align-items-center gap-2 py-1 border-top small" data-share="${escapeHtml(s.shareId)}">
      <span class="badge ${cls}">${label}</span>
      <span class="text-muted">until ${fmtDate(s.expiresAt)}</span>
      <span class="text-muted" title="How often the link has been opened">${s.hits || 0} view${s.hits === 1 ? '' : 's'}</span>
      ${s.state === 'ok'
        ? `<button type="button" class="btn btn-sm btn-link text-danger p-0 ms-auto js-revoke-share">Revoke</button>`
        : ''}
    </div>`;
}

/**
 * Render the share box for one trip.
 * @param {HTMLElement} slot
 * @param {string} assetUUID
 * @param {object} trip  an API trip (needs tripId)
 */
export async function renderTripShare(slot, assetUUID, trip) {
  if (!slot || !trip?.tripId) return;
  const base = `api/v1/assets/${encodeURIComponent(assetUUID)}/trips/${encodeURIComponent(trip.tripId)}/share`;

  const paint = (shares) => {
    const live = shares.filter(s => s.state === 'ok').length;
    slot.innerHTML = `
      <div class="d-flex align-items-center gap-2 mt-2">
        <button type="button" class="btn btn-sm btn-outline-secondary js-create-share">
          <i class="fa-solid fa-link me-1"></i>Share this trip
        </button>
        ${shares.length ? `<span class="small text-muted">${live} active of ${shares.length} link${shares.length === 1 ? '' : 's'}</span>` : ''}
      </div>
      ${shares.length ? `<div class="mt-1">${shares.map(s => linkRow(s)).join('')}</div>` : ''}`;
    wire(shares);
  };

  const wire = (shares) => {
    slot.querySelector('.js-create-share')?.addEventListener('click', () => {
      openTripShareModal({
        tripSummary: summary,
        onCreate: async (scope, days) => {
          const res = await window.authenticatedFetch(base, {
            method: 'POST', headers: { 'content-type': 'application/json' },
            body: JSON.stringify({ scope, days }),
          });
          if (!res.ok) throw new Error('create failed');
          const made = await res.json();
          shares = [...shares, made];
          paint(shares);           // the list behind the modal keeps up
          return made;
        },
      });
    });

    slot.querySelectorAll('.js-revoke-share').forEach(btn => {
      btn.addEventListener('click', async (ev) => {
        const row = ev.currentTarget.closest('[data-share]');
        const id = row?.dataset.share;
        if (!id) return;
        ev.currentTarget.disabled = true;
        try {
          const res = await window.authenticatedFetch(`${base}/${encodeURIComponent(id)}`, { method: 'DELETE' });
          if (!res.ok) throw new Error('revoke failed');
          shares = shares.map(s => s.shareId === id ? { ...s, state: 'revoked' } : s);
          paint(shares);
          window.showToast?.('Link revoked', 'success');
        } catch {
          window.showToast?.('Could not revoke the link', 'danger');
          ev.currentTarget.disabled = false;
        }
      });
    });
  };

  const when = trip.startTime
    ? new Date(trip.startTime).toLocaleString(navigator.language, { dateStyle: 'medium', timeStyle: 'short' })
    : '';
  const summary = [when, trip.distanceM != null ? `${(trip.distanceM / 1000).toFixed(1)} km` : null]
    .filter(Boolean).join(' · ');

  let shares = [];
  try {
    const res = await window.authenticatedFetch(base);
    if (res.ok) shares = (await res.json()).shares || [];
  } catch { /* an unreachable list must not hide the create button */ }
  paint(shares);
}
